import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { StoryListPanel } from 'src/organisms/CollectibleSceneInfo/StoriesListPanel'
import { ArrowLeftRightIcon } from '@/atoms/Icons/ArrowLeftRightIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CaptionSM } from '@/atoms/Text'
import { SmpteController } from '@/organisms/AngelPlayer'
import { useAngelPlayerContext } from '@/organisms/AngelPlayer/AngelPlayerContext/AngelPlayerContext'
import { CollectibleSceneInfo } from '@/organisms/CollectibleSceneInfo'
import { CollectibleGiftMessage } from '@/organisms/CollectibleSceneInfo/CollectibleGiftMessage'
import { useCollectiblesPlayerEngagement } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { useUser } from '@/services/UserService'
import { Frame } from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'
import { usePlayerMoments } from '../../PlayerMomentsProvider'

export interface CollectiblesPlayerEngagementPanelProps {
  currentTime: number
  frame?: Frame
  controller: SmpteController | undefined
}

export const CollectiblesPlayerEngagementPanel: React.FC<CollectiblesPlayerEngagementPanelProps> = ({
  currentTime,
  frame,
  controller,
}) => {
  const {
    isEngagementPanelOpen,
    engagementPanelState,
    engagementMomentDetails,
    defaultEngagementDetail,
    refetchEngagementMomentDetails,
    updateEngagementMoment,
  } = useCollectiblesPlayerEngagement()

  const { showCommunityView, toggleCommunityViewOff } = useAngelPlayerContext()
  const isOwner = Boolean(engagementMomentDetails?.moment?.isOwner)
  const { t } = useTranslate('watch')
  const { userId } = useUser()
  const isFrameOwner = Boolean(
    isOwner && currentTime === defaultEngagementDetail?.startTime && userId === frame?.user?.uuid,
  )
  const [lastProcessedMoment, setLastProcessedMoment] = useState<string | null>(null)
  const gift = frame?.nifty?.gift
  const [showGiftMessage, setShowGiftMessage] = useState(Boolean(gift))

  const { getMoment } = usePlayerMoments()

  useEffect(() => {
    const interval = setInterval(() => {
      if (controller && showCommunityView) {
        const currentTime = controller.getCurrentTime()
        const currentMoment = getMoment({ time: currentTime })

        if (currentMoment) {
          if (lastProcessedMoment !== currentMoment.id) {
            refetchEngagementMomentDetails({
              momentId: currentMoment.id || lastProcessedMoment || '',
              includeMyStory: true,
            })
            updateEngagementMoment(currentMoment)
            setLastProcessedMoment(currentMoment.id || null)
          }
        }
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [
    getMoment,
    refetchEngagementMomentDetails,
    lastProcessedMoment,
    updateEngagementMoment,
    showCommunityView,
    controller,
  ])

  const hasGift = Boolean(gift?.gifter)

  if (!showCommunityView) {
    return null
  }

  return (
    <>
      <div
        className={classNames(
          'relative my-4 w-full transition-all duration-300 ease-in-out md:my-0 lg:w-[350px] xl:w-[400px] 2xl:w-[450px] flex-none mr-0',
          {
            'h-0 overflow-hidden lg:h-auto lg:-mr-[350px] xl:-mr-[400px] 2xl:-mr-[450px] !my-0': !isEngagementPanelOpen,
          },
        )}
        id="engagement-panel"
      >
        <div
          className="absolute -top-1 right-2 z-10 flex cursor-pointer items-center justify-center rounded-full bg-core-gray-900/90 p-2 md:right-4 md:top-0 lg:right-6"
          onClick={toggleCommunityViewOff}
        >
          <CloseIcon size={20} color="gray-300" />
        </div>
        {isFrameOwner && (
          <CaptionSM className="absolute z-[99] hidden w-fit -translate-x-full rounded-[50px] bg-[#0000004D] px-[10px] py-1 font-[600] text-white lg:-left-[24px] lg:top-20 lg:block">
            {t('yourFrame', 'Your Frame')}
          </CaptionSM>
        )}
        {engagementPanelState === 'collectible-scene' && (
          <div className="inset-0 mx-1 my-3 flex-1 overflow-y-auto scrollbar-thin md:my-4 lg:absolute lg:my-0">
            {showGiftMessage && hasGift && gift ? (
              <CollectibleGiftMessage gift={gift} frame={frame?.id} />
            ) : (
              <CollectibleSceneInfo />
            )}
            {gift && (
              <div
                className="mx-auto flex w-fit cursor-pointer items-center gap-1"
                onClick={() => setShowGiftMessage(!showGiftMessage)}
              >
                <ArrowLeftRightIcon size={16} className="mb-1 mr-2" />
                {showGiftMessage ? <CaptionSM>Scene Details</CaptionSM> : <CaptionSM>Gift Details</CaptionSM>}
              </div>
            )}
          </div>
        )}
        {engagementPanelState === 'stories-list' && (
          <div className="inset-0 mx-1 flex-1 overflow-y-auto overflow-x-hidden px-4 py-6 lg:absolute">
            <StoryListPanel />
          </div>
        )}
      </div>
    </>
  )
}

CollectiblesPlayerEngagementPanel.displayName = 'React.memo(CollectiblesPlayerEngagementPanel)'
